// pages/Report/SalonSalesReport.js
import React, { useState, useEffect, useCallback } from 'react';
import{useAuth}from'../../common/context/AuthContext';
import{SALON_API_URL}from'../../api/config';
import { TrendingUp, Hash, Tag, Scissors, Package, Calendar, X, Loader2, Award, BarChart3, Banknote, Smartphone, CreditCard } from 'lucide-react';

function fmt(n) { return '₹' + Number(n||0).toLocaleString('en-IN',{minimumFractionDigits:0,maximumFractionDigits:2}); }
function fmtDate(d) { try { return new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return '—'; } }
function toLocal(s,end=false){const d=new Date(s);if(end)d.setHours(23,59,59,999);else d.setHours(0,0,0,0);return d.toISOString();}
function toDateParam(d){const dt = d instanceof Date ? d : new Date(d); const p=n=>String(n).padStart(2,'0'); return `${dt.getFullYear()}-${p(dt.getMonth()+1)}-${p(dt.getDate())}`;}

const FILTERS=[{id:'today',label:'Today'},{id:'yesterday',label:'Yesterday'},{id:'week',label:'This Week'},{id:'month',label:'This Month'},{id:'3months',label:'Last 3 Months'},{id:'custom',label:'Custom'}];

function getRange(id){
  const n=new Date(),y=n.getFullYear(),m=n.getMonth(),d=n.getDate();
  if(id==='today') return{start:new Date(y,m,d,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  if(id==='yesterday') return{start:new Date(y,m,d-1,0,0,0),end:new Date(y,m,d-1,23,59,59,999)};
  if(id==='week'){const day=n.getDay();return{start:new Date(y,m,d-(day===0?6:day-1),0,0,0),end:new Date(y,m,d,23,59,59,999)};}
  if(id==='month') return{start:new Date(y,m,1,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  if(id==='3months') return{start:new Date(y,m-3,d,0,0,0),end:new Date(y,m,d,23,59,59,999)};
  return null;
}
function rangeLabel(id,custom){
  if(id==='custom'&&custom.start&&custom.end) return`${fmtDate(custom.start)} → ${fmtDate(custom.end)}`;
  const r=getRange(id); return r?`${fmtDate(r.start)} → ${fmtDate(r.end)}`:'';
}
function extractItems(bill){
  if(Array.isArray(bill.items)&&bill.items.length>0) return bill.items;
  const svcs=(bill.services||[]).map(s=>({...s,item_type:'service',item_name:s.item_name||s.service_name||s.name||'Service',item_variant:s.item_variant||s.variant||null,price:parseFloat(s.price||0)}));
  const prods=(bill.products||[]).map(p=>({...p,item_type:'product',item_name:p.item_name||p.product_name||p.name||'../../pages/Product',price:parseFloat(p.price||0),qty:p.qty||p.quantity||1}));
  return[...svcs,...prods];
}

const COLORS=['#7c3aed','#3b82f6','#10b981','#f59e0b','#ef4444','#ec4899','#14b8a6'];

function DonutChart({data,title}){
  const total=data.reduce((s,i)=>s+(i.revenue||0),0);
  if(!total||!data.length) return<p className="text-sm text-gray-400 text-center py-8">No data for this period</p>;
  const R=38,C=2*Math.PI*R;let offset=0;

  // Group items beyond the top 6 into "Others" to show full 100% chart and legend
  let displayData = [];
  if (data.length <= 7) {
    displayData = [...data];
  } else {
    displayData = data.slice(0, 6);
    const othersRevenue = data.slice(6).reduce((s, i) => s + (i.revenue || 0), 0);
    displayData.push({
      name: 'Others',
      revenue: othersRevenue,
    });
  }

  const segs=displayData.map((item,idx)=>{const pct=item.revenue/total,dash=pct*C,rot=(offset/total)*360;offset+=item.revenue;return{...item,dash,rot,color:COLORS[idx%COLORS.length],pct};});
  return(
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative w-36 h-36 flex-shrink-0">
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx="50" cy="50" r={R} fill="none" stroke="#f1f5f9" strokeWidth="10"/>
          {segs.map((s,i)=><circle key={i} cx="50" cy="50" r={R} fill="none" stroke={s.color} strokeWidth="10" strokeDasharray={`${s.dash} ${C-s.dash}`} style={{transformOrigin:'center',transform:`rotate(${s.rot}deg)`}}/>)}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-xs text-gray-400">{title}</p>
          <p className="text-base font-black text-gray-800 dark:text-white">{fmt(total)}</p>
        </div>
      </div>
      <div className="flex-1 space-y-2 w-full">
        {segs.map((s,i)=>(
          <div key={i} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:s.color}}/>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{s.name}</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-gray-400">{(s.pct*100).toFixed(0)}%</span>
              <span className="text-xs font-bold text-gray-800 dark:text-white">{fmt(s.revenue)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({data,color='bg-purple-500',maxBars=10}){
  const items=data.slice(0,maxBars),max=Math.max(...items.map(i=>i.revenue||0),1);
  if(!items.length) return<p className="text-sm text-gray-400 text-center py-8">No data for this period</p>;
  return(
    <div className="space-y-3">
      {items.map((item,i)=>(
        <div key={i} className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-black text-purple-600">#{i+1}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{item.name}</p>
              <span className="text-sm font-black text-gray-700 dark:text-white ml-2 flex-shrink-0">{fmt(item.revenue)}</span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div className={`h-full ${color} rounded-full`} style={{width:`${(item.revenue/max)*100}%`}}/>
            </div>
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0 w-14 text-right">{item.count}×</span>
        </div>
      ))}
    </div>
  );
}

function StatCard({title,value,sub,gradient,icon:Icon}){
  return(
    <div className={`relative overflow-hidden rounded-3xl p-6 ${gradient} shadow-sm`}>
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10"/>
      <div className="relative">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center mb-3"><Icon size={20} className="text-white"/></div>
        <p className="text-xs font-bold text-white/70 uppercase tracking-widest">{title}</p>
        <p className="text-2xl font-black text-white mt-1">{value}</p>
        {sub&&<p className="text-xs text-white/60 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

function CustomDateModal({open,onClose,onApply}){
  const[start,setStart]=useState('');const[end,setEnd]=useState('');const[err,setErr]=useState('');
  if(!open)return null;
  const max=new Date().toISOString().split('T')[0];
  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-gray-900 dark:text-white">Custom Range</h3>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"><X size={16}/></button>
        </div>
        <div className="space-y-4">
          <div><label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">From</label>
            <input type="date" max={max} value={start} onChange={e=>setStart(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"/></div>
          <div><label className="text-xs font-bold text-gray-400 uppercase tracking-widest block mb-1.5">To</label>
            <input type="date" max={max} min={start} value={end} onChange={e=>setEnd(e.target.value)} className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"/></div>
          {err&&<p className="text-xs text-red-500">{err}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600">Cancel</button>
          <button onClick={()=>{if(!start||!end){setErr('Select both dates');return;}if(start>end){setErr('Start must be before end');return;}onApply(start,end);onClose();}} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold">Apply</button>
        </div>
      </div>
    </div>
  );
}

export default function SalonSalesReport(){
  const{userToken:token}=useAuth();
  const[activeFilter,setActiveFilter]=useState('month');
  const[custom,setCustom]=useState({start:'',end:''});
  const[showCustom,setShowCustom]=useState(false);
  const[loading,setLoading]=useState(true);
  const[bills,setBills]=useState([]);

  const fetchData=useCallback(async()=>{
    if(!token)return;setLoading(true);
    try{
      let start,end;
      if(activeFilter==='custom'){if(!custom.start||!custom.end){setLoading(false);return;}start=toLocal(custom.start,false);end=toLocal(custom.end,true);}
      else{const r=getRange(activeFilter);start=r.start.toISOString();end=r.end.toISOString();}
      const fromDate = activeFilter === 'custom' ? custom.start : toDateParam(start);
      const toDate = activeFilter === 'custom' ? custom.end : toDateParam(end);
      const res=await fetch(`${SALON_API_URL}/bills?from_date=${encodeURIComponent(fromDate)}&to_date=${encodeURIComponent(toDate)}&limit=5000`,{headers:{Authorization:`Bearer ${token}`}});
      const data=await res.json();
      setBills(data.success?(data.data||[]):[]);
    }catch(e){console.error(e);setBills([]);}
    finally{setLoading(false);}
  },[token,activeFilter,custom]);

  useEffect(()=>{fetchData();},[fetchData]);

  const totalRevenue=bills.reduce((s,b)=>s+parseFloat(b.total||0),0);
  const totalBills=bills.length;
  const totalDiscount=bills.reduce((s,b)=>s+parseFloat(b.discount||0),0);
  const avgBill=totalBills>0?totalRevenue/totalBills:0;

  let cashAmount = 0, upiAmount = 0, cardAmount = 0;
  bills.forEach(b => {
    const total = parseFloat(b.total || 0);
    if (b.payment_method === 'cash') cashAmount += total;
    else if (b.payment_method === 'upi') upiAmount += total;
    else if (b.payment_method === 'card') cardAmount += total;
    else if (b.payment_method === 'split' && b.payment_split) {
      try {
        const split = typeof b.payment_split === 'string' ? JSON.parse(b.payment_split) : b.payment_split;
        cashAmount += parseFloat(split.cash || 0);
        upiAmount += parseFloat(split.upi || 0);
        cardAmount += parseFloat(split.card || 0);
      } catch (e) {
        cashAmount += total;
      }
    } else {
      cashAmount += total;
    }
  });


  const serviceMap={},productMap={};
  bills.forEach(bill=>{
    extractItems(bill).forEach(item=>{
      const type=item.item_type||item.type;
      if(type==='service'){
        const name=(item.item_name||'Unknown')+(item.item_variant?` · ${item.item_variant}`:'');
        if(!serviceMap[name])serviceMap[name]={name,revenue:0,count:0};
        serviceMap[name].revenue+=parseFloat(item.price||0);serviceMap[name].count+=1;
      }else if(type==='product'){
        const name=item.item_name||'Unknown';
        if(!productMap[name])productMap[name]={name,revenue:0,count:0};
        productMap[name].revenue+=parseFloat(item.price||0)*(item.qty||1);productMap[name].count+=(item.qty||1);
      }
    });
  });

  const topServices=Object.values(serviceMap).sort((a,b)=>b.revenue-a.revenue);
  const topProducts=Object.values(productMap).sort((a,b)=>b.revenue-a.revenue);
  const currentLabel=rangeLabel(activeFilter,custom);

  return(
    <div className="p-6 lg:p-8 w-full min-h-full">
      <div className="mb-7">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white">Sales Report</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Revenue, bills, discounts and top performers</p>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {FILTERS.map(f=>(
          <button key={f.id} onClick={()=>{if(f.id==='custom'){setShowCustom(true);return;}setActiveFilter(f.id);setCustom({start:'',end:''}); }}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border-2 ${activeFilter===f.id?'bg-purple-600 border-purple-600 text-white shadow-lg':'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 hover:border-purple-300'}`}>
            {f.label}
          </button>
        ))}
      </div>
      {currentLabel&&(
        <div className="flex items-center gap-2 mb-6">
          <Calendar size={13} className="text-purple-500"/>
          <span className="text-xs font-semibold text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-3 py-1 rounded-full">Showing: {currentLabel}</span>
        </div>
      )}
      {loading?(
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 size={36} className="animate-spin text-purple-600"/>
          <p className="text-gray-400 font-medium">Loading report…</p>
        </div>
      ):(
        <div className="space-y-6">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard title="Total Revenue" value={fmt(totalRevenue)} sub={`${totalBills} bills`} gradient="bg-gradient-to-br from-purple-600 to-violet-700" icon={TrendingUp}/>
            <StatCard title="Total Bills" value={totalBills} sub={`Avg ${fmt(Math.round(avgBill))}`} gradient="bg-gradient-to-br from-blue-500 to-indigo-600" icon={Hash}/>
            <StatCard title="Discount Given" value={fmt(totalDiscount)} sub="Total discounts" gradient="bg-gradient-to-br from-orange-500 to-amber-600" icon={Tag}/>
            <StatCard title="Avg Bill Value" value={fmt(Math.round(avgBill))} sub="Per bill" gradient="bg-gradient-to-br from-emerald-500 to-teal-600" icon={BarChart3}/>
          </div>

          {/* ── Payment Breakdown Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Banknote size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">Cash Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(cashAmount)}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Smartphone size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">UPI Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(upiAmount)}</p>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-50 dark:bg-violet-900/20 text-violet-650 flex items-center justify-center flex-shrink-0">
                <CreditCard size={24} />
              </div>
              <div>
                <p className="text-xs font-extrabold text-gray-400 uppercase tracking-widest">Card Collection</p>
                <p className="text-xl font-black text-gray-800 dark:text-white mt-1">{fmt(cardAmount)}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
              <div className="flex items-center gap-2 mb-5"><Scissors size={18} className="text-purple-500"/><h2 className="font-bold text-gray-800 dark:text-white">Services by Revenue</h2></div>
              <DonutChart data={topServices} title="Services"/>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
              <div className="flex items-center gap-2 mb-5"><Package size={18} className="text-blue-500"/><h2 className="font-bold text-gray-800 dark:text-white">Products by Revenue</h2></div>
              <DonutChart data={topProducts} title="Products"/>
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
            <div className="flex items-center gap-2 mb-5"><Award size={18} className="text-purple-500"/><h2 className="font-bold text-gray-800 dark:text-white">Top Services Ranked</h2><span className="ml-auto text-xs text-gray-400">by revenue</span></div>
            <BarChart data={topServices} color="bg-purple-500" maxBars={10}/>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 p-6">
            <div className="flex items-center gap-2 mb-5"><Award size={18} className="text-blue-500"/><h2 className="font-bold text-gray-800 dark:text-white">Top Products Sold</h2><span className="ml-auto text-xs text-gray-400">by revenue</span></div>
            <BarChart data={topProducts} color="bg-blue-500" maxBars={10}/>
          </div>
          {totalBills===0&&(
            <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700">
              <TrendingUp size={36} className="mx-auto text-gray-200 mb-3"/>
              <h3 className="text-lg font-black text-gray-400">No bills found</h3>
              <p className="text-sm text-gray-400 mt-1">Try a different time period</p>
            </div>
          )}
        </div>
      )}
      <CustomDateModal open={showCustom} onClose={()=>setShowCustom(false)} onApply={(s,e)=>{setCustom({start:s,end:e});setActiveFilter('custom');}}/>
    </div>
  );
}